import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { DASHBOARD_INPUT_CLASS } from './dashboardHelpers'

export type CheckboxFilterOption = {
  value: string
  label: string
}

type CheckboxFilterDropdownProps = {
  label: string
  options: CheckboxFilterOption[]
  selected: string[]
  onChange: (next: string[]) => void
  /** Texto do botão quando nenhuma opção está marcada (equivale a todas). */
  emptyLabel: string
}

export const CheckboxFilterDropdown: React.FC<CheckboxFilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
}) => {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const summary = React.useMemo(() => {
    if (selected.length === 0) return emptyLabel
    const labels = options
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label)
    if (labels.length === 0) return emptyLabel
    if (labels.length === 1) return labels[0]
    return `${labels[0]} +${labels.length - 1}`
  }, [emptyLabel, options, selected])

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]
    )
  }

  return (
    <div ref={rootRef} className="relative">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`${DASHBOARD_INPUT_CLASS} flex items-center justify-between gap-2 text-left`}
      >
        <span className="truncate">{summary}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
        >
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">Nenhuma opção</p>
          ) : (
            <div className="space-y-0.5">
              {options.map((option) => {
                const checked = selected.includes(option.value)
                return (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(option.value)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="min-w-0 truncate">{option.label}</span>
                  </label>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
